import { EventEmitter, Injectable } from '@angular/core';
import { promotionStatuses } from '@app/core/helpers/constants';
import { ISingleCampaign } from '@app/core/models/core-models';
import { IvrConfigurationsService } from '@app/core/services/ivr-configurations/ivr-configurations.service';
import { PpiService } from '@app/core/services/ppi/ppi.service';
import { TrafficSourcesService } from '@app/core/services/traffic-sources/traffic-sources.service';
import { ScrollableTabMenuItem } from '@app/shared/components/scrollable-tab-menu/scrollable-tab-menu.component';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TopMenuItem } from './campaign-configurations.component';

export type CampaignTypes = 'REGULAR' | 'IVR' | 'PPI' | 'REGULAR-PPI';

export type CampaignConfigurationsSteps = 'STEP_1' | 'STEP_2' | 'STEP_3';

@Injectable({
  providedIn: 'root'
})
export class CampaignConfigurationsService {
  public directlinkAdded = new EventEmitter<void>();
  public promotionMethodAdded = new EventEmitter<void>();

  public isPromotionMethodPending = new Subject<boolean>();

  private readonly campaignTypeSubject$ = new BehaviorSubject<CampaignTypes>('REGULAR');
  private readonly currentCampaignConfigurationsStepSubject$ = new BehaviorSubject<CampaignConfigurationsSteps>('STEP_1');

  private readonly IVR_CAMPAIGNS_IDS = [1054, 1055, 1158];
  private readonly PPI_CAMPAIGNS_IDS = [6977, 6978, 6988, 6991, 7106];

  constructor(
    private readonly ivrConfigurationsService: IvrConfigurationsService,
    private readonly ppiConfigurationsService: PpiService,
    private readonly trafficSourceService: TrafficSourcesService
  ) {}

  public getCurrentCampaignConfigurationsStep$ = (): Observable<CampaignConfigurationsSteps> => {
    return this.currentCampaignConfigurationsStepSubject$.asObservable();
  };

  public getCampaignType$ = (): Observable<CampaignTypes> => {
    return this.campaignTypeSubject$.asObservable();
  };

  public setCampaignType = (campaignType: CampaignTypes) => {
    this.campaignTypeSubject$.next(campaignType);
  };

  public setCurrentCampaignConfigurationsStep = (currentStep: CampaignConfigurationsSteps): void => {
    this.currentCampaignConfigurationsStepSubject$.next(currentStep);
  };

  public checkCurrentStep = (isFirstStepCompleted: boolean, isSecondStepCompleted: boolean): CampaignConfigurationsSteps => {
    let currentStep: CampaignConfigurationsSteps = 'STEP_1';

    if (isFirstStepCompleted && !isSecondStepCompleted) currentStep = 'STEP_2';
    if (isFirstStepCompleted && isSecondStepCompleted) currentStep = 'STEP_3';

    return currentStep;
  };

  public checkCampaignType = (campaignId: number, type: string): CampaignTypes => {
    let campaignType: CampaignTypes = 'REGULAR';

    if (this.IVR_CAMPAIGNS_IDS.includes(Number(campaignId))) campaignType = 'IVR';

    if (this.PPI_CAMPAIGNS_IDS.includes(Number(campaignId))) campaignType = 'PPI';

    if (!this.PPI_CAMPAIGNS_IDS.includes(Number(campaignId)) && type === 'CPA | PPI') campaignType = 'REGULAR-PPI';

    return campaignType;
  };

  public getPromotionStatus = (
    isCampaignActive: boolean
  ): Observable<{ isStepOneCompleted: boolean; currentStep: CampaignConfigurationsSteps; isPending: boolean }> => {
    return this.ivrConfigurationsService.getIvrPromotionStatus().pipe(
      map(({ promotion_status }) => {
        const { active, waiting } = promotionStatuses;

        const isCreated = promotion_status === active;
        const isPending = promotion_status === waiting;

        const isStepOneCompleted = isCreated;

        const currentStep = this.checkCurrentStep(isCreated, isCampaignActive);

        this.isPromotionMethodPending.next(isPending);

        return { isStepOneCompleted, currentStep, isPending };
      })
    );
  };

  public getDirectlinksStatus = (isCampaignActive: boolean): Observable<{ isStepOneCompleted: boolean; currentStep: CampaignConfigurationsSteps }> => {
    return this.ppiConfigurationsService.getPPIDirectLinksList().pipe(
      map(({ data, status_code }) => {
        if (status_code === 200 && data?.ppi_configurations) {
          const { ppi_configurations } = data;

          const hasDirectlinks = ppi_configurations.length > 0;

          const isStepOneCompleted = hasDirectlinks;

          const currentStep = this.checkCurrentStep(hasDirectlinks, isCampaignActive);

          return { isStepOneCompleted, currentStep };
        }

        return { isStepOneCompleted: false, currentStep: 'STEP_1' };
      })
    );
  };

  public getTrafficSourcesStatus(isCampaignActive: boolean): Observable<{ isStepOneCompleted: boolean; currentStep: CampaignConfigurationsSteps }> {
    return this.trafficSourceService.getAllTrafficSources(true).pipe(
      map(trafficSources => {
        if (trafficSources.data?.traffic_sources) {
          const hasTrafficSources = !isEmpty(trafficSources.data?.traffic_sources);
          const isStepOneCompleted = hasTrafficSources;

          const currentStep = this.checkCurrentStep(hasTrafficSources, isCampaignActive);

          return { isStepOneCompleted, currentStep };
        }

        return { isStepOneCompleted: false, currentStep: 'STEP_1' };
      })
    );
  }

  public enableTopMenu = (allAvailableConfigurationsNumber: number, currentCampaign?: ISingleCampaign): ScrollableTabMenuItem[] => {
    return this.initializeTopMenuItems(allAvailableConfigurationsNumber, currentCampaign).map(({ name, ...rest }) => ({
      name,
      ...rest,
      active: name === 'all',
      disabled: name !== 'all'
    }));
  };

  public initializeTopMenuItems(allAvailableConfigurationsNumber: number, currentCampaign?: ISingleCampaign): ScrollableTabMenuItem[] {
    const topMenuItems: ScrollableTabMenuItem[] = [
      {
        name: 'all',
        translation: 'all',
        active: false,
        disabled: false,
        count: allAvailableConfigurationsNumber,
        loading: false
      },
      {
        name: 'url_address',
        translation: 'url_address',
        active: false,
        disabled: true,
        count: 0,
        loading: true
      },
      {
        name: 'banner',
        translation: 'banner',
        active: false,
        disabled: !currentCampaign?.has_banners || true,
        count: 0,
        loading: true
      },
      {
        name: 'deep_link',
        translation: 'deep_link',
        active: false,
        disabled: !currentCampaign?.has_deep_link || true,
        count: 0,
        loading: true
      },
      {
        name: 'postback_url',
        translation: 'postback_url',
        active: false,
        disabled: true,
        count: 0,
        loading: true
      },
      {
        name: 'hidelink',
        translation: 'hidelink',
        active: false,
        disabled: !currentCampaign?.has_hidelink || true,
        count: 0,
        loading: true
      },
      {
        name: 'products',
        translation: 'products',
        active: false,
        disabled: !currentCampaign?.has_products || true,
        count: 0,
        loading: true
      }
    ];

    return topMenuItems;
  }
}
